/**
 * SED variable option registry — the single source that maps an extracted SED
 * variable to its CLOSED list of allowed values, so the Variable Browser's
 * amend form can offer a picklist instead of a free-text box.
 *
 * Two kinds of closed list flow through here:
 *   1. Setup-backed enums — e.g. `technology.data.reactorDesignClass` can only
 *      be one of the reactor-design classes the Project Intent setup picker
 *      exposes. Sourced from the SAME catalog the setup form consumes so a new
 *      catalog entry surfaces in both surfaces and the two can't drift.
 *   2. Standard domain enums — e.g. `grid.data.isoRegion` is one of the US
 *      ISO/RTO markets. Sourced from the same value set the field is populated
 *      with (the `grid.isoRegion` research template's enum).
 *
 * The amend form previously edited every value as free text, so amending an
 * enum meant typing an exact token (`lwr-smr`, `ERCOT`) — the friction flagged
 * across the "add list from setup" and "ISO or RTO dropdown" reports.
 *
 * Keying by full variable path (`${domain}.data.${key}`) keeps the mapping
 * explicit and avoids matching a same-named key in an unrelated domain. Every
 * other variable keeps its free-text input.
 */
import {
  REACTOR_DESIGN_CATALOG,
  REACTOR_DESIGN_CLASS_IDS,
} from '../config/reactorDesignCatalog';

/** One selectable value in an amend picklist. `value` is what gets stored on
 *  the SED; `label` is the human-readable copy. */
export interface SedVariableOption {
  value: string;
  label: string;
}

/**
 * US ISO/RTO markets. `value` matches exactly what the `grid.isoRegion`
 * research template fills the field with (its `enumValues`), so the dropdown
 * round-trips a research-filled value cleanly; labels spell out the acronym.
 * Kept in sync with that template's enum and `resolveIsoFromState`.
 */
const ISO_RTO_OPTIONS: readonly SedVariableOption[] = [
  { value: 'ERCOT', label: 'ERCOT — Texas' },
  { value: 'PJM', label: 'PJM Interconnection' },
  { value: 'MISO', label: 'MISO — Midcontinent' },
  { value: 'CAISO', label: 'CAISO — California' },
  { value: 'SPP', label: 'SPP — Southwest Power Pool' },
  { value: 'NYISO', label: 'NYISO — New York' },
  { value: 'ISO-NE', label: 'ISO-NE — New England' },
  { value: 'Non-RTO', label: 'Non-RTO / bilateral market' },
];

/**
 * Registry of variable path → option-list builder. Builders are lazy so the
 * (static) catalogs are only walked for the handful of variables that have a
 * closed list, and never for the common free-text row.
 *
 * Add an entry here to give another enumerated SED variable a picklist.
 */
const OPTIONS_BY_PATH: Readonly<Record<string, () => SedVariableOption[]>> = {
  // Mirrors the reactor-design-class picker in ProjectIntentForm — the setup
  // list excludes the sentinel 'undecided' ("Not selected") so an amend can
  // only move the value to a real class.
  'technology.data.reactorDesignClass': () =>
    REACTOR_DESIGN_CLASS_IDS
      .filter((id) => id !== 'undecided')
      .map((id) => ({ value: id, label: REACTOR_DESIGN_CATALOG[id].label })),
  // ISO/RTO market serving the site. Auto-filled from the site's state by the
  // research template today; the dropdown lets a user correct/override it.
  'grid.data.isoRegion': () => [...ISO_RTO_OPTIONS],
};

/**
 * The setup-defined option list for a SED variable path, or `undefined` when
 * the variable has no closed list (the amend form falls back to free text).
 */
export function sedVariableOptions(path: string): SedVariableOption[] | undefined {
  const build = OPTIONS_BY_PATH[path];
  return build ? build() : undefined;
}

/**
 * The option list to render for `path` given the row's CURRENT value. If the
 * stored value isn't one of the setup options (a legacy id, a since-removed
 * catalog entry, or the 'undecided' sentinel), it's prepended so the amend form
 * never silently drops the value the user is editing.
 */
export function sedVariableOptionsForValue(
  path: string,
  currentValue: unknown,
): SedVariableOption[] | undefined {
  const options = sedVariableOptions(path);
  if (!options) return undefined;
  if (currentValue == null || currentValue === '') return options;
  const current = String(currentValue);
  if (options.some((o) => o.value === current)) return options;
  // Preserve the out-of-list current value as a leading, self-labelled option.
  return [{ value: current, label: current }, ...options];
}
